import { useState, useCallback, useEffect } from "react";
import {
  CustomerGroupEntity,
  GetCustomerGroupsUseCase,
  CreateCustomerGroupUseCase,
} from "../../domain";
import { CustomerRepositoryImpl } from "../../data/repositories/CustomerRepositoryImpl";

// Create repository instance (singleton)
const customerRepository = new CustomerRepositoryImpl();

// Create use cases
const getCustomerGroupsUseCase = new GetCustomerGroupsUseCase(
  customerRepository
);
const createCustomerGroupUseCase = new CreateCustomerGroupUseCase(
  customerRepository
);

export interface UseCustomerGroupsReturn {
  customerGroups: CustomerGroupEntity[];
  loading: boolean;
  error: string | null;

  fetchGroups: () => Promise<void>;
  createGroup: (groupData: {
    group_name: string;
    group_description?: string;
  }) => Promise<CustomerGroupEntity | null>;
  addGroup: (group: CustomerGroupEntity) => void;
  addCustomerGroup: (group: CustomerGroupEntity) => void; // Alias for backward compatibility

  clearError: () => void;
}

export const useCustomerGroups = (): UseCustomerGroupsReturn => {
  const [customerGroups, setCustomerGroups] = useState<CustomerGroupEntity[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch customer groups
   */
  const fetchGroups = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await getCustomerGroupsUseCase.execute();

      if (!result.success) {
        throw new Error("Failed to fetch customer groups");
      }

      setCustomerGroups(result.data);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to fetch customer groups";
      setError(errorMessage);
      console.error("Failed to fetch customer groups:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new customer group
   */
  const createGroup = useCallback(
    async (groupData: {
      group_name: string;
      group_description?: string;
    }): Promise<CustomerGroupEntity | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await createCustomerGroupUseCase.execute(groupData);

        if (!result.success || !result.data) {
          throw new Error("Failed to create customer group");
        }

        // Add to local state
        setCustomerGroups((prev) => [...prev, result.data]);

        return result.data;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to create customer group";
        setError(errorMessage);
        console.error("Failed to create customer group:", error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Manually add a group to the list (for optimistic updates)
   */
  const addGroup = useCallback((group: CustomerGroupEntity): void => {
    setCustomerGroups((prev) => [...prev, group]);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Load customer groups on mount
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return {
    customerGroups,
    loading,
    error,
    fetchGroups,
    createGroup,
    addGroup,
    addCustomerGroup: addGroup, // Alias for backward compatibility
    clearError,
  };
};
